import { useState } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import http from "../helper/http-client";
import { handleLidartsAchievement, updatePlayerResult } from "../helper/result";
import socket from "../socket";
import { updateAllUsers, fetchUserInfo } from "../app/authSlice";

const errMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err.data === "string" && err.data.trim()) return err.data;
  return (
    err.data?.message ||
    err.data?.error ||
    err.message ||
    fallback
  );
};

const gameLinkFromPayload = (data) => {
  const hashid = data?.matchResult?.hashid;
  if (hashid) return `https://lidarts.org/game/${hashid}`;
  return "";
};

const isSameLidartsGame = (resultDoc, data) => {
  const hashid = data?.matchResult?.hashid;
  const link = String(resultDoc?.link || "");
  if (hashid && link.toLowerCase().includes(String(hashid).toLowerCase())) {
    return true;
  }
  const begin = data?.begin || data?.matchResult?.begin;
  if (!begin || !resultDoc?.date) return false;
  return new Date(resultDoc.date).getTime() === new Date(begin).getTime();
};

export const useFightResult = (token, type) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authData = useSelector((state) => state.auth);
  const { user } = authData;

  const [isLoading, setIsLoading] = useState(false);
  const [lidartsUrl, setLidartsUrl] = useState("");
  const [fightResult, setFightResult] = useState({});
  const [earnedAchievement, setEarnedAchievement] = useState({
    user1: [],
    user2: [],
  });
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [currentEarned, setCurrentEarned] = useState(null);
  const [isSaved, setIsSaved] = useState(true);

  const fetchResultData = async () => {
    const parts = String(lidartsUrl || "")
      .trim()
      .split("/");
    // https://lidarts.org/game/HASH → https://lidarts.org/api/game/HASH
    if (parts.length >= 5 && parts[3] !== "api") {
      parts.splice(3, 0, "api");
    }
    // http-client get(url, queryParams) — flach ?url=… (nicht params[url])
    return await http.get("/result/lidart-get", { url: parts.join("/") });
  };

  const getEarnedAchievement = (updatedResult, username, allData) => {
    const lowerCaseUsername = username?.toLowerCase();
    const userResult = updatedResult.find(
      (val) => val?.username?.toLowerCase() === lowerCaseUsername
    );
    const allUserResult = allData?.allResults?.find(
      (val) => val?.username?.toLowerCase() === lowerCaseUsername
    );

    let userIndex = 0;

    if (allData?.user1?.name?.toLowerCase() === lowerCaseUsername) {
      userIndex = 1;
    } else if (allData?.user2?.name?.toLowerCase() === lowerCaseUsername) {
      userIndex = 2;
    }

    return handleLidartsAchievement(
      userResult,
      allUserResult,
      allData,
      userIndex
    );
  };

  const getMyInitFromPayload = (payload) => {
    const myName = user?.username?.toLowerCase();
    if (!myName || !payload) return null;
    if (payload?.user1?.name?.toLowerCase() === myName) return payload.user1.init;
    if (payload?.user2?.name?.toLowerCase() === myName) return payload.user2.init;
    return payload?.allResults?.find(
      (val) => val?.username?.toLowerCase() === myName
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(lidartsUrl || "").trim()) {
      toast.warning("Bitte eine Lidarts-URL einfügen.");
      return;
    }
    setIsLoading(true);

    try {
      const res = await fetchResultData();
      const payload = res.data;
      setFightResult(payload);
      const updatedResult = updatePlayerResult(payload);
      setUpdateResult(updatedResult);

      const earnedAchievement1 = getEarnedAchievement(
        updatedResult,
        payload?.user1?.name,
        payload
      );
      const earnedAchievement2 = getEarnedAchievement(
        updatedResult,
        payload?.user2?.name,
        payload
      );

      setEarnedAchievement({
        user1: earnedAchievement1,
        user2: earnedAchievement2,
      });

      if (
        user?.username?.toLowerCase() === payload?.user1?.name?.toLowerCase()
      )
        setCurrentEarned(earnedAchievement1);
      else setCurrentEarned(earnedAchievement2);

      const myResult = getMyInitFromPayload(payload);
      const already =
        Boolean(payload?.alreadySaved) || isSameLidartsGame(myResult, payload);

      if (already) {
        toast(t("thisGameHasAlreadyBeenSaved"));
      } else {
        setIsSummaryModalOpen(true);
      }
    } catch (err) {
      console.log(err);
      toast.warning(errMessage(err, "Lidarts-Ergebnis konnte nicht geladen werden."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.warning(t("youAreNotAuthorizedToSaveTheGameResults"));
      return;
    }

    if (!updateResult) {
      toast.warning(t("gameResultsCannotBeSaved"));
      return;
    }

    const meInMatch = updateResult.find(
      (val) => val?.username?.toLowerCase() === user?.username?.toLowerCase()
    );
    if (!meInMatch) {
      toast.warning(
        `Dein FightClub-Name (${user?.username || "?"}) steht nicht in diesem Lidarts-Spiel.`
      );
      return;
    }

    const myResult = getMyInitFromPayload(fightResult);
    if (isSameLidartsGame(myResult, fightResult) || fightResult?.alreadySaved) {
      toast(t("thisGameHasAlreadyBeenSaved"));
      setIsSaveModalOpen(true);
      return;
    }

    setIsLoading(true);

    try {
      await http.post("/statistic/increment");
      let savedOk = 0;
      for (const val of updateResult) {
        try {
          await http.post("/result/post", { data: val, token, type });
          await http.post("/auth/update-xp-and-rank", {
            username: val.username,
            xpToAdd: val.previousWin
              ? 100 + Number(val?.earnedAchievements?.length) * 10
              : 50 + Number(val?.earnedAchievements?.length) * 10,
          });
          savedOk += 1;
        } catch (err) {
          toast.error(
            errMessage(
              err,
              `Speichern fehlgeschlagen für ${val?.username || "Spieler"}`
            ),
            { toastId: `save-${val?.username || "x"}` }
          );
        }
      }
      if (savedOk > 0) {
        const toastId = "token-success";
        if (!toast.isActive(toastId)) {
          toast.success(t("gameResultsSavedSuccessfully"), { toastId });
        }
        setIsSaveModalOpen(true);
      }
      const names = updateResult.map((val) => val?.username).filter(Boolean);
      if (names.length) {
        socket.emit("release-challenge-lock", { usernames: names });
      }
    } catch (err) {
      toast.error(errMessage(err, t("anErrorOccurredWhileSavingTheData")));
    } finally {
      setIsLoading(false);
    }
  };

  const onChange = (e) => {
    e.preventDefault();
    setLidartsUrl(e.target.value);
  };

  const onCloseSummaryMdal = () => {
    setIsSummaryModalOpen(false);
  };

  const updateUserStatus = async () => {
    try {
      if (token && !isSaved) {
        await http.post("/result/abort-match", { token });
      }
    } catch (err) {
      console.log("abort-match:", err);
    }
    socket.emit("update-user-status", {
      status: "online",
      userID: user?._id,
    });
    socket.emit("release-challenge-lock", {
      usernames: [
        user?.username,
        fightResult?.user1?.name,
        fightResult?.user2?.name,
      ].filter(Boolean),
    });

    dispatch(updateAllUsers({ ...user, status: "online" }));
  };

  const calcMatchXp = (val) =>
    val?.previousWin
      ? 100 + Number(val?.earnedAchievements?.length || 0) * 10
      : 50 + Number(val?.earnedAchievements?.length || 0) * 10;

  const matchXp = (() => {
    if (!updateResult?.length) return { user1: 0, user2: 0 };
    const u1 = updateResult.find(
      (val) =>
        val?.username?.toLowerCase() === fightResult?.user1?.name?.toLowerCase()
    );
    const u2 = updateResult.find(
      (val) =>
        val?.username?.toLowerCase() === fightResult?.user2?.name?.toLowerCase()
    );
    return {
      user1: u1 ? calcMatchXp(u1) : 0,
      user2: u2 ? calcMatchXp(u2) : 0,
    };
  })();

  const currentMatchXp = (() => {
    if (!updateResult?.length || !user?.username) return 0;
    const mine = updateResult.find(
      (val) => val?.username?.toLowerCase() === user.username.toLowerCase()
    );
    return mine ? calcMatchXp(mine) : 0;
  })();

  const onCloseSaveModal = () => {
    setIsSaveModalOpen(false);

    const names = (updateResult || []).map((val) => val?.username).filter(Boolean);
    if (names.length) {
      socket.emit("release-challenge-lock", { usernames: names });
    }

    updateUserStatus();

    dispatch(fetchUserInfo());

    setIsSaved(false);

    navigate("/pyramid-users");
  };

  return {
    isLoading,
    lidartsUrl,
    isSaved,
    fightResult,
    earnedAchievement,
    isSummaryModalOpen,
    isSaveModalOpen,
    currentEarned,
    matchXp,
    currentMatchXp,
    onChange,
    updateUserStatus,
    onCloseSummaryMdal,
    onCloseSaveModal,
    handleSubmit,
    handleSave,
    gameLink: gameLinkFromPayload(fightResult),
  };
};
